import { Dialog, FormGroup, H4, HTMLSelect, NumericInput, Icon } from '@blueprintjs/core'
import { Colors } from 'design/colors'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { ThrottleRangeDisplay } from 'components/ThrottleRangeDisplay'
import { hidSend } from 'connections/hid'
import {
  serializeSetControlModeRequest,
  serializeSetUsbThrottleControlRequest,
  ControlMode as ControlModeEnum
} from 'utils/common-types'
import {
  ControlMode,
  IWriteControlConfigRequest,
  IWritePowerConfigResponse,
  InputMode,
  IWritePositionControlSettingsRequest,
  PositionType,
  PositionResolution,
  PositionUnits,
  HomingCalibStatus,
  IZeroDareSensorResponse
} from 'utils/common-types'
import {
  Strings,
  isPolePairCountValid as _isPolePairCountValid,
  P_GAIN_TEXT,
  I_GAIN_TEXT,
  D_GAIN_TEXT,
  MOTOR_MAX_SPEED_TEXT,
  MOTOR_MAX_ACCELERATION_TEXT,
  DEADBAND_TEXT,
  POSITION_TYPE_TEXT,
  POSITION_UNITS_TEXT,
  RESOLUTION_TEXT,
  TRAVEL_DISTANCE_TEXT
} from 'utils/common'
import { InputConfig, PosCtrlCalResults } from 'utils/firestore'
import { MIN_RPM_STEP, MIN_STEP, physicalToDevice, SPEED_MODE_DEVICE_RANGE } from 'utils/throttle'
import './Config.css'
import { useSingleTelemetryValue } from 'hooks/useSingleTelemetryValue'
import { Button } from 'components/Button'
import { ToggleButton } from 'components/toggleButton/ToggleButton'
import { Tooltip2 } from '@blueprintjs/popover2'
import {
  centerItemsStyle,
  configContainerStyle,
  configModalStyle,
  INavigationProps,
  inputStyle,
  positionButtonContStyle,
  screenTitleStyle
} from './CStoreEditor'
import {
  readDareZeroResult,
  runHomingCalibration,
  zeroDareSensor,
  setPositionLimitToCurrentPos
} from 'connections/controller'
import { PlotKey } from 'utils/packet.worker'
import { useDevices } from 'hooks/useDevices'
import { JOG_HOMING_FEATURE, useUserFeatures } from 'setup/UserFeaturesContext'
import { getPositionUnitLabel } from 'hooks/useThrottleConfig'
import { SubActuators } from 'utils/slnt-file'
import { useSubActuators } from 'hooks/useSubActuators'

const FORM_WIDTH = 350
const JOG_IP_CENTER = 2500

const homingStatusToString = (status: HomingCalibStatus): string => {
  switch (status) {
    case HomingCalibStatus.SUCCESS:
      return 'Success'
    case HomingCalibStatus.FAILURE_TIMEOUT:
      return 'Failed: Timeout'
    case HomingCalibStatus.FAILURE_NO_STALL:
      return 'Failed: No stall detected'
    case HomingCalibStatus.FAILURE_NOT_FREEWHEEL:
      return 'Failed: Motor not in freewheel'
    case HomingCalibStatus.IN_PROGRESS:
      return 'In Progress...'
    default:
      return 'Unknown'
  }
}

// Visual feedback for the soft-stop range. Shows the full calibrated mechanical
// scope as a track with the soft-stop window highlighted, plus a live cursor
// for the motor's current position. Labels in the device's configured units;
// hides mrad/raw numbers from the human-facing UI.
const SoftStopRangeWidget = ({
  pctStr,
  travelDistanceStr,
  rawPosition,
  calMin,
  calMax
}: {
  pctStr: string
  travelDistanceStr: string
  rawPosition: number | null
  calMin: number | undefined
  calMax: number | undefined
}) => {
  const pct = parseInt(pctStr, 10)
  const td = parseFloat(travelDistanceStr)
  if (!isFinite(pct) || !isFinite(td) || td <= 0) return null
  const clampedPct = Math.max(0, Math.min(100, pct))
  const unit = getPositionUnitLabel()
  const unitSuffix = unit ? ` ${unit}` : ''
  const center = td / 2
  const halfWidth = ((clampedPct / 100) * td) / 2
  const lo = center - halfWidth
  const hi = center + halfWidth
  // Hide the soft-min/max labels when they collide with the end labels.
  const showSoftLabels = clampedPct > 0 && clampedPct < 92

  const fmt = (v: number) => {
    const abs = Math.abs(v)
    const decimals = abs >= 100 ? 1 : abs >= 10 ? 2 : 3
    return v.toFixed(decimals)
  }

  // Map the live position onto the track. The mechanical bar is a *linear*
  // travel display, so the cursor must be driven by the linear sensor reading
  // (rawPositionSensor) — NOT the motor's angular position (positionMrad), and
  // NOT divided by travelDistance (which is in physical in/mm, a different unit
  // than the raw sensor; dividing the two pinned the cursor to 0%/100%). We
  // normalize the raw reading between the calibrated min/max endpoints captured
  // during jog homing (same telemetry source), giving a unitless 0..1 fraction
  // that maps cleanly onto [0, td].
  const haveCal =
    calMin !== undefined &&
    calMax !== undefined &&
    isFinite(calMin) &&
    isFinite(calMax) &&
    calMin !== calMax
  const haveLivePos = haveCal && rawPosition !== null && isFinite(rawPosition)
  const frac = haveLivePos ? (rawPosition! - calMin!) / (calMax! - calMin!) : null
  const livePhysical = frac === null ? null : frac * td // position in [0, td] axis units
  const livePct = frac === null ? null : Math.max(0, Math.min(100, frac * 100))
  const inSoftWindow = livePhysical !== null && livePhysical >= lo && livePhysical <= hi
  const cursorColor = inSoftWindow ? '#ffd166' : '#ff6b6b'

  return (
    <div style={{ position: 'relative', height: 150, marginBottom: 8, marginTop: 4 }}>
      {/* Caption row */}
      <span
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          fontSize: 14,
          color: Colors.WHITE,
          opacity: 0.7,
          letterSpacing: 0.5,
          textTransform: 'uppercase'
        }}
      >
        Mechanical range
      </span>
      <span
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          fontSize: 14,
          color: Colors.LIGHT_GREEN,
          opacity: 0.95,
          fontWeight: 'bold'
        }}
      >
        Soft-stop window: {clampedPct}%
      </span>

      {/* Track block (positioned below caption, with room above for cursor) */}
      <div style={{ position: 'absolute', top: 60, left: 0, right: 0, height: 22 }}>
        {/* Calibrated mechanical range — full-width gray track */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 22,
            backgroundColor: '#3a3a3a',
            borderRadius: 11,
            border: `1px solid ${Colors.LIGHT_GREEN}`
          }}
        />
        {/* Soft-stop window — highlighted segment */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            height: 22,
            left: `${50 - clampedPct / 2}%`,
            width: `${clampedPct}%`,
            backgroundColor: Colors.LIGHT_GREEN,
            borderRadius: 11,
            transition: 'left 120ms ease, width 120ms ease',
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)'
          }}
        />
        {/* Center marker — vertical tick */}
        <div
          style={{
            position: 'absolute',
            top: -6,
            height: 34,
            left: '50%',
            width: 2,
            backgroundColor: Colors.WHITE,
            opacity: 0.6,
            transform: 'translateX(-1px)'
          }}
        />
      </div>

      {/* Live position cursor (only when we have a reading). Rendered as a
          downward-pointing triangle above the track so it visually "lands" on
          the bar; color changes when out of the soft window. */}
      {livePct !== null && (
        <>
          <div
            style={{
              position: 'absolute',
              top: 38,
              left: `${livePct}%`,
              transform: 'translate(-50%, 0)',
              width: 0,
              height: 0,
              borderLeft: '11px solid transparent',
              borderRight: '11px solid transparent',
              borderTop: `16px solid ${cursorColor}`,
              transition: 'left 80ms linear, border-top-color 200ms ease',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))'
            }}
          />
          {/* Position label */}
          <span
            style={{
              position: 'absolute',
              top: 18,
              left: `${livePct}%`,
              transform: 'translateX(-50%)',
              fontSize: 16,
              fontWeight: 'bold',
              color: cursorColor,
              whiteSpace: 'nowrap'
            }}
          >
            {fmt(livePhysical!)}
            {unitSuffix}
          </span>
        </>
      )}

      {/* End-point labels: 0 and travelDistance */}
      <span
        style={{
          position: 'absolute',
          top: 90,
          left: 0,
          fontSize: 15,
          color: Colors.WHITE,
          opacity: 0.85,
          fontWeight: 500
        }}
      >
        {fmt(0)}
        {unitSuffix}
      </span>
      <span
        style={{
          position: 'absolute',
          top: 90,
          right: 0,
          fontSize: 15,
          color: Colors.WHITE,
          opacity: 0.85,
          fontWeight: 500
        }}
      >
        {fmt(td)}
        {unitSuffix}
      </span>
      {/* Center label */}
      <span
        style={{
          position: 'absolute',
          top: 90,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 14,
          color: Colors.WHITE,
          opacity: 0.6
        }}
      >
        {fmt(center)}
      </span>

      {/* Soft min / max labels (hidden when ~100% to avoid overlap with ends) */}
      {showSoftLabels && (
        <>
          <span
            style={{
              position: 'absolute',
              top: 116,
              left: `${50 - clampedPct / 2}%`,
              transform: 'translateX(-50%)',
              fontSize: 16,
              color: Colors.LIGHT_GREEN,
              fontWeight: 'bold'
            }}
          >
            {fmt(lo)}
            {unitSuffix}
          </span>
          <span
            style={{
              position: 'absolute',
              top: 116,
              left: `${50 + clampedPct / 2}%`,
              transform: 'translateX(-50%)',
              fontSize: 16,
              color: Colors.LIGHT_GREEN,
              fontWeight: 'bold'
            }}
          >
            {fmt(hi)}
            {unitSuffix}
          </span>
        </>
      )}
    </div>
  )
}

// Renders one SoftStopRangeWidget for a single sub-actuator, subscribing to
// that motor's own rawPositionSensor telemetry. Split out from the parent so
// each motor gets its own useSingleTelemetryValue call (hook rules disallow a
// dynamic number of hook calls in a loop within one component).
const MotorSoftStopRangeWidget = ({
  nodeId,
  pctStr,
  travelDistanceStr,
  calMin,
  calMax
}: {
  nodeId: number
  pctStr: string
  travelDistanceStr: string
  calMin: number | undefined
  calMax: number | undefined
}) => {
  const rawPosition = useSingleTelemetryValue(
    `MultiMotor_N${nodeId}_positionSensorRawValue` as PlotKey
  ) as number | undefined
  return (
    <div style={{ padding: 10, borderRadius: 8, border: `1px solid ${Colors.LIGHT_GREEN}` }}>
      <div
        style={{
          marginBottom: 6,
          fontSize: 13,
          color: Colors.WHITE,
          opacity: 0.9,
          fontWeight: 700,
          letterSpacing: 0.4
        }}
      >
        Motor {nodeId} Position
      </div>
      <SoftStopRangeWidget
        pctStr={pctStr}
        travelDistanceStr={travelDistanceStr}
        rawPosition={rawPosition ?? null}
        calMin={calMin}
        calMax={calMax}
      />
    </div>
  )
}

type Edit<T> = (key: keyof T, value: string) => void

export const isPolePairCountValid = (polePairCount: string | number): boolean => {
  const ppc = typeof polePairCount === 'string' ? parseFloat(polePairCount) : polePairCount
  return _isPolePairCountValid(ppc)
}

const throttleErrors = (
  inputConfig: Strings<InputConfig>,
  controlConfig: Strings<IWriteControlConfigRequest>
): string[] => {
  type StepSizeInfo = {
    label: string
    step: number
    stepStr: string | undefined
    units: string
    minStep: number
  }
  const stepSizes: StepSizeInfo[] = [
    {
      label: 'torque',
      step: parseFloat(inputConfig.ampsStep ?? ''),
      stepStr: inputConfig.ampsStep,
      units: 'A',
      minStep: MIN_STEP
    },
    {
      label: 'speed',
      step: parseFloat(inputConfig.rpmStep ?? ''),
      stepStr: inputConfig.rpmStep,
      units: 'RPM',
      minStep: MIN_RPM_STEP
    }
  ]

  const createStepSizeErrorStr = (info: StepSizeInfo) => {
    const prefix = `Throttle step size for ${info.label} control mode`
    if (info.stepStr === '') {
      return `${prefix} must be supplied.`
    } else if (!isFinite(info.step)) {
      return `${prefix} is ${info.stepStr} ${info.units} which is invalid.`
    } else {
      return `${prefix} is ${info.stepStr} ${info.units} which is less than minimum of ${info.minStep} ${info.units}.`
    }
  }

  const isInputModeUSB = inputConfig.inputMode === InputMode.USB.toString()
  let errors
  const areStepSizesValidated = isInputModeUSB
  if (areStepSizesValidated) {
    errors = stepSizes
      .filter((info: StepSizeInfo) => !isFinite(info.step) || info.step < info.minStep)
      .map(createStepSizeErrorStr)

    const controlMode: ControlMode = parseInt(controlConfig.controlMode, 10)
    const motorMaxSpeed = parseFloat(controlConfig.motorMaxSpeed) // Call this "Max Throttle Speed" in the UI

    if (controlMode === ControlMode.SPEED) {
      if (!isFinite(motorMaxSpeed)) {
        errors.push('Max Throttle Speed is an invalid value.')
      } else if (inputConfig.rpmStep !== undefined) {
        const step = parseFloat(inputConfig.rpmStep)
        if (isFinite(step) && motorMaxSpeed < step) {
          errors.push('Throttle Step Size cannot exceed Max Throttle Speed')
        }
      }
    }
  } else {
    errors = []

    // ToDo: improve errors below
    const { inputMode, inputLimitLower, inputLimitCenter, inputLimitUpper } = inputConfig
    if ([inputMode, inputLimitLower, inputLimitCenter, inputLimitUpper].some((v) => v === '')) {
      errors.push(
        'One or more of these are blank: input mode, lower, center, and/or upper input limit.'
      )
    } else if (
      parseInt(inputLimitLower, 10) > parseInt(inputLimitCenter, 10) ||
      parseInt(inputLimitCenter, 10) > parseInt(inputLimitUpper, 10) ||
      parseInt(inputLimitLower, 10) < 0
    ) {
      errors.push('Input limits must be as follows: 0 <= lower <= center <= upper.')
    }
  }

  if (!isPolePairCountValid(controlConfig.polePairCount)) {
    errors.push('Pole pair count must be a positive integer.')
  }

  return errors
}

export const InputScreen = (
  props: INavigationProps & {
    inputConfig: Strings<InputConfig>
    powerConfig: Strings<IWritePowerConfigResponse>
    controlConfig: Strings<IWriteControlConfigRequest>
    positionControlSettings: Strings<IWritePositionControlSettingsRequest>
    posCtrlCalResultsStorable: boolean // Indicates whether the fields for storing calibration results are in cstore
    posCtrlCalResults: PosCtrlCalResults | undefined // Current calibration results (min/max from jog homing, in raw sensor units)
    setPosCtrlCalResults: Dispatch<SetStateAction<PosCtrlCalResults | undefined>>
    editInputConfig: Edit<InputConfig>
    editControlConfig: Edit<IWriteControlConfigRequest>
    editPositionControlSettings: Edit<IWritePositionControlSettingsRequest>
    setWarningContents: (contents: ReactNode) => void
  }
) => {
  const {
    inputConfig,
    powerConfig,
    controlConfig,
    positionControlSettings,
    posCtrlCalResultsStorable,
    posCtrlCalResults,
    setPosCtrlCalResults,
    editInputConfig,
    editControlConfig,
    editPositionControlSettings,
    setWarningContents,
    prev,
    next,
    attached
  } = props

  const lowerGreaterThanCenter =
    inputConfig.inputLimitLower !== '' &&
    inputConfig.inputLimitCenter !== '' &&
    parseInt(inputConfig.inputLimitLower, 10) > parseInt(inputConfig.inputLimitCenter, 10)
  const centerGreaterThanUpper =
    inputConfig.inputLimitCenter !== '' &&
    inputConfig.inputLimitUpper !== '' &&
    parseInt(inputConfig.inputLimitCenter, 10) > parseInt(inputConfig.inputLimitUpper, 10)

  const [selectedControlMode, setSelectedControlMode] = useState(controlConfig.controlMode)
  const [selectedPositionType, setSelectedPositionType] = useState(
    positionControlSettings.positionControlType
  )
  const [selectedPostitionUnits, setSelectedPositionUnits] = useState(
    positionControlSettings.positionUnits
  )
  const [selectedPositionResolution, setSelectedPositionResolution] = useState(
    positionControlSettings.positionResolution
  )
  const [showMinPositionModal, setShowMinPositionModal] = useState<boolean>(false)
  const [showMaxPositionModal, setShowMaxPositionModal] = useState<boolean>(false)
  const [showZeroSensorErrorModal, setShowZeroSensorErrorModal] = useState<boolean>(false)
  const [showJogWarningModal, setShowJogWarningModal] = useState<boolean>(false)

  // Homing state
  const { hasFeature } = useUserFeatures()
  const jogHomingEnabled = hasFeature(JOG_HOMING_FEATURE)
  const { connectedDevice } = useDevices()
  const [isHoming, setIsHoming] = useState(false)

  // Jog Homing state
  const [isJogHoming, setIsJogHoming] = useState(false)
  const [jogThrottle, setJogThrottle] = useState<string>('0')
  const jogThrottleRef = useRef<number>(0)
  const [jogHomingMax, setJogHomingMax] = useState<number | null>(null)
  const [jogHomingMin, setJogHomingMin] = useState<number | null>(null)
  const [prevControlMode, setPrevControlMode] = useState<string | null>(null)
  // Jog safety: require explicit Start before sending commands
  const [jogEnabled, setJogEnabled] = useState(false)
  // Declare positionSensorValue above any use
  const [positionSensorValue, setPositionSensorValue] = useState<number | null>(null)
  const [selectedMotorNodeId, setSelectedMotorNodeId] = useState<number | null>(null)
  const selectedMotorIndex = selectedMotorNodeId ?? undefined

  // Raw position sensor comes from a given motor in multimotor mode and from
  // REALTIME_DATA in actuator-fw mode.
  const rawPositionSensorKey = (
    selectedMotorIndex !== undefined
      ? `MultiMotor_N${selectedMotorIndex}_positionSensorRawValue`
      : 'rawPositionSensor'
  ) as PlotKey
  const rawPositionSensor = useSingleTelemetryValue(rawPositionSensorKey) as number | undefined
  const rawPositionSensorStr = rawPositionSensor?.toFixed(3) ?? '-----'

  // Send throttle command (RPM) for selected motor, with unit conversion for jog
  const sendThrottleCommand = useCallback(
    async (physicalRpm: number) => {
      if (!connectedDevice?.handle) return
      // Use physicalToDevice for USB input mode, else passthrough
      let deviceRpm = physicalRpm
      try {
        if (inputConfig.inputMode === InputMode.USB.toString()) {
          // Firmware forces this fixed range whenever control mode is SPEED
          // (see SPEED_MODE_DEVICE_RANGE) — jog throttle must scale against
          // it directly rather than the device's cstore input limits.
          const maxSpeed = parseFloat(controlConfig.motorMaxSpeed)
          if (isFinite(maxSpeed) && maxSpeed > 0) {
            deviceRpm = physicalToDevice(
              InputMode.USB,
              SPEED_MODE_DEVICE_RANGE,
              maxSpeed,
              physicalRpm,
              ControlMode.SPEED
            )
          }
        }
      } catch (e) {
        console.error('Failed to convert throttle command; sending fallback value as-is.', e)
      }
      await hidSend(
        connectedDevice.handle,
        serializeSetUsbThrottleControlRequest({
          value: Math.max(0, deviceRpm),
          motorIndex: selectedMotorIndex ?? 0
        })
      )
    },
    [connectedDevice, selectedMotorIndex, inputConfig, controlConfig]
  )

  // Throttle handler: just update state, interval will send command
  const handleJogThrottleChange = (val: number) => {
    setJogThrottle(String(val))
    jogThrottleRef.current = val
  }

  const handleStopJogCommand = useCallback(async () => {
    setJogEnabled(false)
    setJogThrottle('0')
    await sendThrottleCommand(0)
  }, [sendThrottleCommand])

  useEffect(() => {
    const parsed = parseFloat(jogThrottle)
    if (isFinite(parsed)) {
      jogThrottleRef.current = parsed
    }
  }, [jogThrottle])

  const sendJogPulse = useCallback(() => {
    if (!isJogHoming || !jogEnabled) return
    sendThrottleCommand(jogThrottleRef.current)
  }, [isJogHoming, jogEnabled, sendThrottleCommand])

  // Only send jog command when jogEnabled is true (after pressing Start in Jog Homing)
  useEffect(() => {
    if (!isJogHoming || !jogEnabled) return
    sendJogPulse()
    const interval = setInterval(sendJogPulse, 50)
    return () => clearInterval(interval)
  }, [isJogHoming, jogEnabled, sendJogPulse])

  // Start jog homing: switch to speed mode, enable UI, send to firmware (per-motor)
  const handleStartJogHoming = async () => {
    setPrevControlMode(controlConfig.controlMode)
    setJogThrottle('0') // Always reset jogThrottle to 0 before starting
    if (controlConfig.controlMode !== ControlMode.SPEED.toString()) {
      editControlConfig('controlMode', ControlMode.SPEED.toString())
      // Send SET_CONTROL_MODE to firmware (global)
      if (connectedDevice?.handle) {
        const dv = serializeSetControlModeRequest({ controlMode: ControlModeEnum.SPEED })
        await hidSend(connectedDevice.handle, dv)
      }
      // Wait a short time to let firmware/UI catch up
      await new Promise((res) => setTimeout(res, 100))
    }
    setIsJogHoming(true)
    setJogHomingMax(null)
    setJogHomingMin(null)
    setJogEnabled(false) // Show Start button inside Jog Homing
    // Immediately command neutral so the motor does not move on entering jog
    // homing. In jog space 0 rpm maps to JOG_IP_CENTER (2500); we send it
    // directly here because isJogHoming has not yet flushed to sendThrottleCommand.
    if (connectedDevice?.handle) {
      const dv = serializeSetUsbThrottleControlRequest({
        value: JOG_IP_CENTER,
        motorIndex: selectedMotorIndex ?? 0
      })
      await hidSend(connectedDevice.handle, dv)
    }
  }

  // Stop jog homing: send 0 throttle and restore previous control mode, send to firmware (per-motor)
  const handleStopJogHoming = async () => {
    await handleStopJogCommand()
    setIsJogHoming(false)
    if (prevControlMode && prevControlMode !== ControlMode.SPEED.toString()) {
      editControlConfig('controlMode', prevControlMode)
      if (connectedDevice?.handle) {
        const dv = serializeSetControlModeRequest({ controlMode: parseInt(prevControlMode, 10) })
        await hidSend(connectedDevice.handle, dv)
      }
    }
  }

  const handleConfirmStartJogHoming = async () => {
    setShowJogWarningModal(false)
    await handleStartJogHoming()
  }

  // No interval logic needed for jog homing anymore
  // Throttle command is sent only when slider is moved or jog ends

  // UI: Motor selection dropdown (if multi-motor supported)
  const subActuators: SubActuators = useSubActuators()
  const motorOptions = useMemo(() => {
    return Object.values(subActuators)
      .map((actuatorInfo) => ({
        value: String(actuatorInfo.nodeId),
        label: `Motor ${actuatorInfo.nodeId}`
      }))
      .sort((a, b) => Number(a.value) - Number(b.value))
  }, [subActuators])

  useEffect(() => {
    if (motorOptions.length === 0) {
      setSelectedMotorNodeId(null)
      return
    }
    const selectedStillExists =
      selectedMotorNodeId !== null &&
      motorOptions.some((option) => Number(option.value) === selectedMotorNodeId)
    if (!selectedStillExists) {
      const firstNodeId = Number(motorOptions[0].value)
      setSelectedMotorNodeId(Number.isFinite(firstNodeId) ? firstNodeId : null)
    }
  }, [motorOptions, selectedMotorNodeId])

  // Jog Homing: Set Min = zero sensor, set minPosition = 0, store encoder zeros
  const handleSetMin = async () => {
    if (!connectedDevice?.handle || rawPositionSensor === undefined) return
    setJogHomingMin(rawPositionSensor) // min is now current sensor value
    setMinPosition(rawPositionSensor) // update minPosition state as well
    setPosCtrlCalResults((orig) => ({
      minPosition: rawPositionSensor,
      maxPosition: orig?.maxPosition ?? jogHomingMax ?? NaN,
      dareZeroEncoder0: orig?.dareZeroEncoder0,
      dareZeroEncoder1: orig?.dareZeroEncoder1,
      dareCountDirection: orig?.dareCountDirection
    }))
    // Only send to device
    await setPositionLimitToCurrentPos(connectedDevice.handle, true, false, selectedMotorIndex)
  }

  // Jog Homing: Set Max = read current, store as maxPosition, update direction
  const handleSetMax = async () => {
    // After setting max, update input limits for position mode UI
    if (rawPositionSensor === undefined) return
    if (!connectedDevice?.handle) return
    setJogHomingMax(rawPositionSensor)
    setMaxPosition(rawPositionSensor) // update maxPosition state as well
    setPosCtrlCalResults((orig) => ({
      minPosition: orig?.minPosition ?? jogHomingMin ?? NaN,
      maxPosition: rawPositionSensor,
      dareZeroEncoder0: orig?.dareZeroEncoder0,
      dareZeroEncoder1: orig?.dareZeroEncoder1,
      dareCountDirection: orig?.dareCountDirection
    }))
    // Only send to device
    await setPositionLimitToCurrentPos(connectedDevice.handle, false, true, selectedMotorIndex)
  }
  const [homingStatus, setHomingStatus] = useState<HomingCalibStatus | null>(null)
  const [homingZeroOffset, setHomingZeroOffset] = useState<number | null>(null)
  const [minPosition, setMinPosition] = useState<number | null>(null)
  const [maxPosition, setMaxPosition] = useState<number | null>(null)

  // Calibrated stroke endpoints (in raw sensor units) that drive the soft-stop
  // graphic's live cursor. Sourced ONLY from posCtrlCalResults, which is always
  // captured from rawPositionSensor (jog homing Set Min/Max and the DARE-zero
  // handler). We intentionally do NOT fall back to the local minPosition/
  // maxPosition state — that state is also written from positionMrad (angular)
  // during automatic homing, so it can be in a different unit than the cursor.
  const calMinForGraphic = posCtrlCalResults?.minPosition
  const calMaxForGraphic = posCtrlCalResults?.maxPosition

  // Position sensor reading for homing. rawPositionSensor is already reactive
  // (pushed by useSingleTelemetryValue as telemetry arrives), so this just
  // mirrors it into display/min-max state instead of polling it on a timer.
  useEffect(() => {
    if (rawPositionSensor === undefined) return
    setPositionSensorValue(rawPositionSensor)
    if (isHoming) {
      setMinPosition((prev) =>
        prev === null ? rawPositionSensor : Math.min(prev, rawPositionSensor)
      )
      setMaxPosition((prev) =>
        prev === null ? rawPositionSensor : Math.max(prev, rawPositionSensor)
      )
    }
  }, [rawPositionSensor, isHoming])

  // Auto-derive IP_Upper/Lower/Center from softStopRangePercent + travelDistance
  // + positionResolution whenever any of those change. This is the single
  // source of truth for the Leidos slider's commandable range — typing a new
  // travel distance, applying a fresh cstore (e.g. after homing), or bumping
  // the percent all reflow into the IP_* form fields without manual editing.
  // Skips no-op writes so it doesn't fight the user mid-typing or churn the
  // form's dirty state.
  useEffect(() => {
    if (selectedControlMode !== ControlMode.POSITION.toString()) return
    const pct = parseInt(positionControlSettings.softStopRangePercent ?? '100', 10)
    const td = parseFloat(positionControlSettings.travelDistance)
    const res = parseInt(positionControlSettings.positionResolution, 10)
    if (!isFinite(pct) || !isFinite(td) || !isFinite(res) || td <= 0) return
    const factor = Math.pow(10, res)
    const center = td / 2
    const halfWidth = ((Math.max(0, Math.min(100, pct)) / 100) * td) / 2
    const lower = String(Math.round((center - halfWidth) * factor))
    const centerStr = String(Math.round(center * factor))
    const upper = String(Math.round((center + halfWidth) * factor))
    if (inputConfig.inputLimitLower !== lower) editInputConfig('inputLimitLower', lower)
    if (inputConfig.inputLimitCenter !== centerStr) editInputConfig('inputLimitCenter', centerStr)
    if (inputConfig.inputLimitUpper !== upper) editInputConfig('inputLimitUpper', upper)
    // editInputConfig is intentionally omitted from deps — Edit<T> is recreated
    // each render; including it would re-run the effect every render and stomp
    // any in-flight typing into the % field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    positionControlSettings.softStopRangePercent,
    positionControlSettings.travelDistance,
    positionControlSettings.positionResolution,
    selectedControlMode,
    inputConfig.inputLimitLower,
    inputConfig.inputLimitCenter,
    inputConfig.inputLimitUpper
  ])

  // Homing callback
  const handleStartHoming = useCallback(async () => {
    if (!connectedDevice?.handle) return

    // Reset state
    setIsHoming(true)
    setHomingStatus(HomingCalibStatus.IN_PROGRESS)
    setHomingZeroOffset(null)
    setMinPosition(null)
    setMaxPosition(null)

    try {
      const result = await runHomingCalibration(connectedDevice.handle)
      setHomingStatus(result.status)
      setHomingZeroOffset(result.zeroOffset)
    } catch (error) {
      console.error('Homing failed:', error)
      setHomingStatus(HomingCalibStatus.FAILURE_TIMEOUT)
    } finally {
      setIsHoming(false)
    }
  }, [connectedDevice?.handle])

  const isInputModeUSB = inputConfig.inputMode === InputMode.USB.toString()
  const isDisplayingPhysicalUnits = isInputModeUSB
  const isControlModeSpeed = selectedControlMode === ControlMode.SPEED.toString()
  const isControlModePosition = selectedControlMode === ControlMode.POSITION.toString()
  // Check device's actual control mode (from cstore region) for homing visibility
  const isDeviceInPositionMode =
    connectedDevice?.initialized && connectedDevice.controlMode === ControlMode.POSITION
  const handleControlModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const controlMode = e.target.value
    setSelectedControlMode(controlMode)
    editControlConfig('controlMode', controlMode)
  }

  const handlePositionTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const positionType = e.target.value
    setSelectedPositionType(positionType)
    editPositionControlSettings('positionControlType', positionType)
  }

  const handlePositionUnitsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const positionUnits = e.target.value
    setSelectedPositionUnits(positionUnits)
    editPositionControlSettings('positionUnits', positionUnits)
  }

  const handlePositionResolutionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const positionResolution = e.target.value
    setSelectedPositionResolution(positionResolution)
    editPositionControlSettings('positionResolution', positionResolution)
    editInputConfig(
      'inputLimitUpper',
      calculateInputResolution(
        parseInt(positionResolution),
        parseInt(positionControlSettings.travelDistance)
      )
    )
  }

  const handleTravelDistanceChange = (value: number, valueStr: string) => {
    // setSelectedTravelDistance(valueStr);
    editPositionControlSettings('travelDistance', valueStr)
    editInputConfig(
      'inputLimitUpper',
      calculateInputResolution(parseInt(selectedPositionResolution), value)
    )
  }

  const calculateInputResolution = (resolution: number, travelDistance: number): string => {
    switch (resolution) {
      case PositionResolution.VALUE_1E3:
        travelDistance *= 1000 // Thousandths
        break
      case PositionResolution.VALUE_1E2:
        travelDistance *= 100 // Hundredths
        break
      case PositionResolution.VALUE_1E1:
        travelDistance *= 10 // Tenths
        break
      default:
        travelDistance *= 1 // Ones
        break
    }
    return travelDistance.toString()
  }

  let stepFieldLabel: string | undefined
  let stepField: keyof InputConfig | undefined

  switch (selectedControlMode) {
    case ControlMode.SPEED.toString():
      stepFieldLabel = 'Throttle Step Size (RPM)'
      stepField = 'rpmStep'
      break
    case ControlMode.TORQUE.toString():
      stepFieldLabel = 'Throttle Step Size (Amps)'
      stepField = 'ampsStep'
      break
  }

  const throttleErrorsMemo = useMemo(
    () => throttleErrors(inputConfig, controlConfig),
    [inputConfig, controlConfig]
  )

  useEffect(() => {
    if (throttleErrorsMemo.length === 0) {
      setWarningContents(null)
      return
    }

    const inputWarning = (
      <>
        <H4>Invalid Values</H4>
        <ul style={{ color: Colors.WHITE }}>
          {throttleErrorsMemo.map((error, i) => (
            <li key={i}>{error}</li>
          ))}
        </ul>
      </>
    )
    setWarningContents(inputWarning)
  }, [setWarningContents, throttleErrorsMemo])

  const controlModeOptions = [
    { value: ControlMode.TORQUE, label: 'Torque' },
    { value: ControlMode.SPEED, label: 'Speed' },
    { value: ControlMode.POSITION, label: 'Position' }
  ]

  const positionTypeOptions = [
    { value: PositionType.UNKNOWN, label: 'Select' },
    { value: PositionType.LINEAR, label: 'Linear' },
    { value: PositionType.ROTATIONAL, label: 'Rotational' }
  ]

  const positionUnitsOptions =
    selectedPositionType === PositionType.LINEAR.toString()
      ? [
          { value: PositionUnits.UNKNOWN, label: 'Select' },
          { value: PositionUnits.MILLIMETERS, label: 'Millimeters' },
          { value: PositionUnits.INCHES, label: 'Inches' }
        ]
      : selectedPositionType === PositionType.ROTATIONAL.toString()
        ? [
            { value: PositionUnits.UNKNOWN, label: 'Select' },
            { value: PositionUnits.RADIANS, label: 'Radians' },
            { value: PositionUnits.DEGREES, label: 'Degrees' }
          ]
        : [
            {
              value: PositionUnits.UNKNOWN,
              label: 'Select Position Type First'
            }
          ]

  const positionResolutionOptions = [
    { value: PositionResolution.UNKNOWN, label: 'Select' },
    { value: PositionResolution.VALUE_1E0, label: 'Ones' },
    { value: PositionResolution.VALUE_1E1, label: 'Tenths' },
    { value: PositionResolution.VALUE_1E2, label: 'Hundredths' },
    { value: PositionResolution.VALUE_1E3, label: 'Thousandths' }
  ]

  const setMinPositionWithDareSensor = useCallback(async () => {
    setShowMinPositionModal(false)
    if (!connectedDevice?.handle) return

    // Zero sensor in RAM (no CStore flash, no reboot).
    await zeroDareSensor(connectedDevice.handle, true)
    await new Promise((r) => setTimeout(r, 200))
    let readback: IZeroDareSensorResponse | undefined
    let attempts = 2
    while (!readback && attempts--) {
      readback = await readDareZeroResult(connectedDevice.handle)
    }

    if (!readback) {
      setShowZeroSensorErrorModal(true)
      return
    }

    // Set minPosition to always be zero, which is what the DARE sensor zeroing ensures
    setPosCtrlCalResults((orig) => ({
      minPosition: 0,
      maxPosition: orig?.maxPosition ?? NaN,
      dareZeroEncoder0: readback!.encoder0ZeroPos,
      dareZeroEncoder1: readback!.encoder1ZeroPos,
      dareCountDirection: orig?.dareCountDirection
    }))
  }, [connectedDevice, setPosCtrlCalResults])

  return (
    <div style={configContainerStyle}>
      <div style={screenTitleStyle}>Throttle Settings</div>
      <div style={{ display: 'flex', flexDirection: 'row', gap: 32, alignItems: 'flex-start' }}>
        {/* Left side - Throttle config */}
        <div style={{ width: FORM_WIDTH, maxWidth: '100%', marginBottom: 8 }}>
          {/* INPUT CONFIG OPTIONS */}
          {/* INPUT MODE */}
          <FormGroup label="Input Mode" labelInfo={<span style={{ color: Colors.RED }}>*</span>}>
            <HTMLSelect
              style={inputStyle}
              fill={true}
              value={inputConfig.inputMode}
              options={[
                { value: InputMode.NONE, label: 'None' },
                { value: InputMode.CAN, label: 'CAN' },
                { value: InputMode.SERVO, label: 'Servo' },
                { value: InputMode.USB, label: 'USB' }
              ]}
              onChange={(e) => editInputConfig('inputMode', e.target.value)}
            />
          </FormGroup>

          {/* CONTROL MODE CONFIGURATION OPTIONS */}

          {/* POLE PAIR COUNT */}
          <FormGroup
            label="Pole Pair Count"
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
          >
            <NumericInput
              fill={true}
              value={controlConfig.polePairCount}
              onValueChange={(_, valStr) => editControlConfig('polePairCount', valStr)}
              stepSize={
                1e-15 /* accept any floating point for now; integer validation happens later */
              }
              minorStepSize={null}
              buttonPosition="none"
              placeholder="Pole Pair Count"
              style={inputStyle}
            />
          </FormGroup>

          {/* CONTROL MODE */}
          <FormGroup label="Control Mode" labelInfo={<span style={{ color: Colors.RED }}>*</span>}>
            <HTMLSelect
              style={inputStyle}
              fill={true}
              value={selectedControlMode}
              options={controlModeOptions}
              onChange={handleControlModeChange}
            />
          </FormGroup>

          {/* MAX THROTTLE SPEED */}
          {isControlModeSpeed && (
            <FormGroup
              label="Max Throttle Speed (RPM)"
              labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            >
              <NumericInput
                fill={true}
                value={controlConfig.motorMaxSpeed}
                onValueChange={(_, valStr) => editControlConfig('motorMaxSpeed', valStr)}
                stepSize={1e-15 /* accept any floating point */}
                minorStepSize={null}
                buttonPosition="none"
                placeholder="Max Throttle Speed"
                style={inputStyle}
              />
            </FormGroup>
          )}

          {/* SPEED CONTROL PROPORTIONAL GAIN */}
          {(isControlModeSpeed || isControlModePosition) && (
            <FormGroup
              label="Speed Control Proportional Gain (N-m/s/(mech. rad))"
              labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            >
              <NumericInput
                disabled={isControlModePosition}
                fill={true}
                value={controlConfig.speedControlProportionalGain}
                onValueChange={(_, valStr) =>
                  editControlConfig('speedControlProportionalGain', valStr)
                }
                stepSize={1e-15 /* accept any floating point */}
                minorStepSize={null}
                buttonPosition="none"
                placeholder="Speed Control Proportional Gain"
                style={inputStyle}
              />
            </FormGroup>
          )}

          {/* SPEED CONTROL INTEGRAL GAIN */}
          {(isControlModeSpeed || isControlModePosition) && (
            <FormGroup
              label="Speed Control Integral Gain (N-m/(mech. rad))"
              labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            >
              <NumericInput
                disabled={isControlModePosition}
                fill={true}
                value={controlConfig.speedControlIntegralGain}
                onValueChange={(_, valStr) => editControlConfig('speedControlIntegralGain', valStr)}
                stepSize={1e-15 /* accept any floating point */}
                minorStepSize={null}
                buttonPosition="none"
                placeholder="Speed Control Integral Gain"
                style={inputStyle}
              />
            </FormGroup>
          )}

          {/* Position P Gain (float: 0-10.0) */}
          {isControlModePosition && (
            <FormGroup
              label={
                <>
                  Position P Gain <span style={{ color: Colors.RED }}>*</span>
                </>
              }
              labelInfo={
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Tooltip2 content={P_GAIN_TEXT}>
                    <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                  </Tooltip2>
                </div>
              }
            >
              <NumericInput
                fill={true}
                value={positionControlSettings.positionControlProportionalGain}
                onValueChange={(_, valStr) =>
                  editPositionControlSettings('positionControlProportionalGain', valStr)
                }
                stepSize={1e-15 /* accept any floating point */}
                minorStepSize={null}
                buttonPosition="none"
                placeholder="Position Control Proportional Gain"
                style={inputStyle}
              />
            </FormGroup>
          )}

          {/* Position I Gain (float: 0-10.0) */}
          {isControlModePosition && (
            <FormGroup
              label={
                <>
                  Position I Gain <span style={{ color: Colors.RED }}>*</span>
                </>
              }
              labelInfo={
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Tooltip2 content={I_GAIN_TEXT}>
                    <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                  </Tooltip2>
                </div>
              }
            >
              <NumericInput
                fill={true}
                value={positionControlSettings.positionControlIntegralGain}
                onValueChange={(_, valStr) =>
                  editPositionControlSettings('positionControlIntegralGain', valStr)
                }
                stepSize={1e-15 /* accept any floating point */}
                minorStepSize={null}
                buttonPosition="none"
                placeholder="Position Control Integral Gain"
                style={inputStyle}
              />
            </FormGroup>
          )}

          {/* Position D Gain (float: 0-10.0) */}
          {isControlModePosition && (
            <FormGroup
              label={
                <>
                  Position D Gain <span style={{ color: Colors.RED }}>*</span>
                </>
              }
              labelInfo={
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Tooltip2 content={D_GAIN_TEXT}>
                    <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                  </Tooltip2>
                </div>
              }
            >
              <NumericInput
                fill={true}
                value={positionControlSettings.positionControlDerivGain}
                onValueChange={(_, valStr) =>
                  editPositionControlSettings('positionControlDerivGain', valStr)
                }
                stepSize={1e-15 /* accept any floating point */}
                minorStepSize={null}
                buttonPosition="none"
                placeholder="Position Control Derivative Gain"
                style={inputStyle}
              />
            </FormGroup>
          )}

          {/* Max Speed (rad/s) (float: 0-3000.0) */}
          {isControlModePosition && (
            <FormGroup
              label={
                <>
                  Max Speed <span style={{ color: Colors.RED }}>*</span>
                </>
              }
              labelInfo={
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Tooltip2 content={MOTOR_MAX_SPEED_TEXT}>
                    <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                  </Tooltip2>
                </div>
              }
            >
              <NumericInput
                fill={true}
                value={positionControlSettings.positionControlMaxSpeed}
                onValueChange={(_, valStr) =>
                  editPositionControlSettings('positionControlMaxSpeed', valStr)
                }
                stepSize={1e-15 /* accept any floating point */}
                minorStepSize={null}
                buttonPosition="none"
                placeholder="Max Speed"
                style={inputStyle}
              />
            </FormGroup>
          )}

          {/* Max Acceleration (rad/s^2) (float: 0-30000.0) */}
          {isControlModePosition && (
            <FormGroup
              label={
                <>
                  Max Acceleration <span style={{ color: Colors.RED }}>*</span>
                </>
              }
              labelInfo={
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Tooltip2 content={MOTOR_MAX_ACCELERATION_TEXT}>
                    <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                  </Tooltip2>
                </div>
              }
            >
              <NumericInput
                fill={true}
                value={positionControlSettings.positionControlMaxAccel}
                onValueChange={(_, valStr) =>
                  editPositionControlSettings('positionControlMaxAccel', valStr)
                }
                stepSize={1e-15 /* accept any floating point */}
                minorStepSize={null}
                buttonPosition="none"
                placeholder="Max Acceleration"
                style={inputStyle}
              />
            </FormGroup>
          )}

          {/* Deadband (rad) (float: 0-10.0) */}
          {isControlModePosition && (
            <FormGroup
              label={
                <>
                  Deadband <span style={{ color: Colors.RED }}>*</span>
                </>
              }
              labelInfo={
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Tooltip2 content={DEADBAND_TEXT}>
                    <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                  </Tooltip2>
                </div>
              }
            >
              <NumericInput
                fill={true}
                value={positionControlSettings.positionControlDeadband}
                onValueChange={(_, valStr) =>
                  editPositionControlSettings('positionControlDeadband', valStr)
                }
                stepSize={1e-15 /* accept any floating point */}
                minorStepSize={null}
                buttonPosition="none"
                placeholder="Deadband"
                style={inputStyle}
              />
            </FormGroup>
          )}

          {/* POSITION TYPE */}
          {isControlModePosition && (
            <FormGroup
              label={
                <>
                  Position Type <span style={{ color: Colors.RED }}>*</span>
                </>
              }
              labelInfo={
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Tooltip2 content={POSITION_TYPE_TEXT}>
                    <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                  </Tooltip2>
                </div>
              }
            >
              <HTMLSelect
                style={inputStyle}
                fill={true}
                value={selectedPositionType}
                options={positionTypeOptions}
                onChange={handlePositionTypeChange}
              />
            </FormGroup>
          )}

          {/* POSITION UNITS */}
          {isControlModePosition && (
            <FormGroup
              label={
                <>
                  Position Units <span style={{ color: Colors.RED }}>*</span>
                </>
              }
              labelInfo={
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Tooltip2 content={POSITION_UNITS_TEXT}>
                    <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                  </Tooltip2>
                </div>
              }
            >
              <HTMLSelect
                style={inputStyle}
                fill={true}
                value={selectedPostitionUnits}
                options={positionUnitsOptions}
                onChange={handlePositionUnitsChange}
              />
            </FormGroup>
          )}

          {/* RESOLUTION */}
          {isControlModePosition && (
            <FormGroup
              label={
                <>
                  Resolution <span style={{ color: Colors.RED }}>*</span>
                </>
              }
              labelInfo={
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Tooltip2 content={RESOLUTION_TEXT}>
                    <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                  </Tooltip2>
                </div>
              }
            >
              <HTMLSelect
                style={inputStyle}
                fill={true}
                value={selectedPositionResolution}
                options={positionResolutionOptions}
                onChange={handlePositionResolutionChange}
              />
            </FormGroup>
          )}

          {/* Travel Distance */}
          {isControlModePosition && (
            <FormGroup
              label={
                <>
                  Travel Distance <span style={{ color: Colors.RED }}>*</span>
                </>
              }
              labelInfo={
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Tooltip2 content={TRAVEL_DISTANCE_TEXT}>
                    <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                  </Tooltip2>
                </div>
              }
            >
              <NumericInput
                fill={true}
                value={positionControlSettings.travelDistance}
                onValueChange={handleTravelDistanceChange}
                stepSize={1e-15 /* accept any floating point */}
                minorStepSize={null}
                buttonPosition="none"
                placeholder="Travel Distance"
                style={inputStyle}
              />
            </FormGroup>
          )}

          {/* Calibrate Min position */}
          {isControlModePosition && (
            <div style={positionButtonContStyle}>
              <div style={{ flex: 1 }}>
                <Tooltip2
                  content="A device must be actively connected to use this function"
                  disabled={attached}
                  targetTagName="div"
                >
                  <Button
                    backgroundColor={Colors.WHITE}
                    textColor={Colors.BLACK}
                    disabled={!posCtrlCalResultsStorable || !attached}
                    onClick={() => setShowMinPositionModal(true)}
                  >
                    Calibrate Min position
                  </Button>
                </Tooltip2>
              </div>
            </div>
          )}

          {isControlModePosition && (
            <Dialog
              title="Calibrate Min Position"
              isOpen={showMinPositionModal}
              onClose={() => setShowMinPositionModal(false)}
              style={{ backgroundColor: Colors.LIGHT_GREEN }}
            >
              <div style={configModalStyle}>
                <div style={centerItemsStyle}>
                  <h2>Move the motor to the minimum position and click Set.</h2>
                </div>
                <div style={centerItemsStyle}>
                  <h2>{rawPositionSensorStr}</h2>
                </div>
                <div style={centerItemsStyle}>
                  <Button
                    backgroundColor={Colors.WHITE}
                    textColor={Colors.BLACK}
                    onClick={setMinPositionWithDareSensor}
                  >
                    Set
                  </Button>
                </div>
              </div>
            </Dialog>
          )}

          {/* Calibrate Max position */}
          {isControlModePosition && (
            <div style={positionButtonContStyle}>
              <div style={{ flex: 1 }}>
                <Tooltip2
                  content="A device must be actively connected to use this function"
                  disabled={attached}
                  targetTagName="div"
                >
                  <Button
                    backgroundColor={Colors.WHITE}
                    textColor={Colors.BLACK}
                    disabled={!posCtrlCalResultsStorable || !attached}
                    onClick={() => setShowMaxPositionModal(true)}
                  >
                    Calibrate Max position
                  </Button>
                </Tooltip2>
              </div>
            </div>
          )}

          {isControlModePosition && (
            <Dialog
              title="Calibrate Max Position"
              isOpen={showMaxPositionModal}
              onClose={() => setShowMaxPositionModal(false)}
              style={{ backgroundColor: Colors.LIGHT_GREEN }}
            >
              <div style={configModalStyle}>
                <div style={centerItemsStyle}>
                  <h2>Move the motor to the maximum position and click Set.</h2>
                </div>
                <div style={centerItemsStyle}>
                  <h2>{rawPositionSensorStr}</h2>
                </div>
                <div style={centerItemsStyle}>
                  <Button
                    backgroundColor={Colors.WHITE}
                    textColor={Colors.BLACK}
                    onClick={async () => {
                      setShowMaxPositionModal(false)
                      if (rawPositionSensor === undefined || !connectedDevice?.handle) return

                      // Always update calibration state, regardless of firmware command success
                      setPosCtrlCalResults((orig) => {
                        const maxPos = rawPositionSensor
                        // DARE zero is at the min position, so travel direction is the sign of max
                        const direction = maxPos >= 0 ? 1 : -1
                        return {
                          minPosition: orig?.minPosition ?? NaN,
                          maxPosition: maxPos,
                          dareZeroEncoder0: orig?.dareZeroEncoder0,
                          dareZeroEncoder1: orig?.dareZeroEncoder1,
                          dareCountDirection: direction
                        }
                      })
                    }}
                  >
                    Set
                  </Button>
                </div>
              </div>
            </Dialog>
          )}

          {/* Zero Sensor Error Modal */}
          <Dialog
            title="Sensor Zero Error"
            isOpen={showZeroSensorErrorModal}
            onClose={() => setShowZeroSensorErrorModal(false)}
            style={{ backgroundColor: Colors.LIGHT_GREEN }}
          >
            <div style={configModalStyle}>
              <div style={centerItemsStyle}>
                <h2>Failed to read sensor zero offsets.</h2>
              </div>
              <div style={centerItemsStyle}>
                <p>Please try again or check the device connection.</p>
              </div>
              <div style={centerItemsStyle}>
                <Button
                  backgroundColor={Colors.WHITE}
                  textColor={Colors.BLACK}
                  onClick={() => setShowZeroSensorErrorModal(false)}
                >
                  OK
                </Button>
              </div>
            </div>
          </Dialog>

          {/* MTPA */}
          <FormGroup label="MTPA">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <span
                style={{
                  fontWeight: 'bold',
                  textShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)'
                }}
              >
                {parseInt(controlConfig.mtpa) === 1 ? 'ENABLED' : 'DISABLED'}
              </span>
              <ToggleButton
                value={parseInt(controlConfig.mtpa)}
                onChange={(value: number) => {
                  editControlConfig('mtpa', String(value))
                }}
              />
            </div>
          </FormGroup>

          {/* Flux Weakening */}
          <FormGroup label="Flux Weakening">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <span
                style={{
                  fontWeight: 'bold',
                  textShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)'
                }}
              >
                {parseInt(controlConfig.fluxWeakening) === 1 ? 'ENABLED' : 'DISABLED'}
              </span>
              <ToggleButton
                value={parseInt(controlConfig.fluxWeakening)}
                onChange={(value: number) => {
                  editControlConfig('fluxWeakening', String(value))
                }}
              />
            </div>
          </FormGroup>

          {/* INPUT STEPS OR MAPPING */}
          {!isControlModePosition ? (
            isDisplayingPhysicalUnits ? (
              <FormGroup label={stepFieldLabel}>
                <NumericInput
                  fill={true}
                  value={inputConfig[stepField!]}
                  onValueChange={(_, valStr) => editInputConfig(stepField!, valStr)}
                  stepSize={1e-15 /* accept any floating point */}
                  minorStepSize={null}
                  buttonPosition="none"
                  placeholder={stepFieldLabel}
                  style={inputStyle}
                  disabled={stepField === undefined}
                />
              </FormGroup>
            ) : (
              <FormGroup
                label="Input Mapping"
                labelInfo={<span style={{ color: Colors.RED }}>*</span>}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1, marginRight: 8 }}>
                    <NumericInput
                      fill={true}
                      value={inputConfig.inputLimitLower}
                      onValueChange={(_, valStr) =>
                        editInputConfig('inputLimitLower', valStr.replaceAll(/[.-]/g, ''))
                      }
                      stepSize={
                        1e-15 /* accept any floating point for now; integer validation happens later */
                      }
                      minorStepSize={null}
                      buttonPosition="none"
                      placeholder="Lower"
                      style={inputStyle}
                    />
                  </div>
                  <div
                    style={{
                      fontWeight: 'bold',
                      fontSize: 16,
                      color: lowerGreaterThanCenter ? Colors.RED : undefined
                    }}
                  >
                    {'<='}
                  </div>
                  <div style={{ flex: 1, marginLeft: 8, marginRight: 8 }}>
                    <NumericInput
                      fill={true}
                      value={inputConfig.inputLimitCenter}
                      onValueChange={(_, valStr) =>
                        editInputConfig('inputLimitCenter', valStr.replaceAll(/[.-]/g, ''))
                      }
                      stepSize={
                        1e-15 /* accept any floating point for now; integer validation happens later */
                      }
                      minorStepSize={null}
                      buttonPosition="none"
                      placeholder="Center"
                      style={inputStyle}
                    />
                  </div>
                  <div
                    style={{
                      fontWeight: 'bold',
                      fontSize: 16,
                      color: centerGreaterThanUpper ? Colors.RED : undefined
                    }}
                  >
                    {'<='}
                  </div>
                  <div style={{ flex: 1, marginLeft: 8 }}>
                    <NumericInput
                      fill={true}
                      value={inputConfig.inputLimitUpper}
                      onValueChange={(_, valStr) =>
                        editInputConfig('inputLimitUpper', valStr.replaceAll(/[.-]/g, ''))
                      }
                      stepSize={
                        1e-15 /* accept any floating point for now; integer validation happens later */
                      }
                      minorStepSize={null}
                      buttonPosition="none"
                      placeholder="Upper"
                      style={inputStyle}
                    />
                  </div>
                </div>
              </FormGroup>
            )
          ) : null}
          {selectedControlMode !== ControlMode.POSITION.toString() ? (
            <ThrottleRangeDisplay
              powerConfig={powerConfig}
              controlConfig={controlConfig}
              inputConfig={inputConfig}
            />
          ) : null}
        </div>

        {/* Vertical Divider - only show if device is in position mode */}
        {isDeviceInPositionMode && (
          <div style={{ width: 1, backgroundColor: Colors.LIGHT_GREEN, alignSelf: 'stretch' }} />
        )}

        {/* Right side - Homing (only visible when device's cstore has Position control mode) */}
        {isDeviceInPositionMode && (
          <div style={{ width: FORM_WIDTH, maxWidth: '100%' }}>
            <H4 style={{ marginBottom: 16, color: Colors.WHITE }}>Homing & Soft Stops</H4>

            {/* Visualization: full mechanical range, soft-stop window, live cursor.
                Replaces the previous raw mrad readouts so operators see where the
                actuator sits relative to its limits at a glance. */}
            {positionControlSettings.softStopRangePercent !== undefined ? (
              <div style={{ marginBottom: 16 }}>
                {Object.keys(subActuators).length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                    {Object.values(subActuators).map((actuatorInfo) => (
                      <MotorSoftStopRangeWidget
                        key={actuatorInfo.nodeId}
                        nodeId={actuatorInfo.nodeId}
                        pctStr={positionControlSettings.softStopRangePercent ?? '100'}
                        travelDistanceStr={positionControlSettings.travelDistance}
                        calMin={calMinForGraphic}
                        calMax={calMaxForGraphic}
                      />
                    ))}
                  </div>
                ) : (
                  <SoftStopRangeWidget
                    pctStr={positionControlSettings.softStopRangePercent}
                    travelDistanceStr={positionControlSettings.travelDistance}
                    rawPosition={rawPositionSensor ?? null}
                    calMin={calMinForGraphic}
                    calMax={calMaxForGraphic}
                  />
                )}
                <FormGroup
                  label="Soft Stop Range"
                  labelInfo={
                    <span style={{ opacity: 0.6 }}>(% of mechanical range, centered)</span>
                  }
                  style={{ marginTop: 8 }}
                >
                  <NumericInput
                    fill={true}
                    value={positionControlSettings.softStopRangePercent}
                    onValueChange={(numVal, valStr) => {
                      const clamped = Math.max(0, Math.min(100, Math.round(numVal)))
                      const pctStr = isFinite(clamped) ? String(clamped) : valStr
                      editPositionControlSettings('softStopRangePercent', pctStr)
                    }}
                    min={0}
                    max={100}
                    stepSize={1}
                    minorStepSize={null}
                    buttonPosition="right"
                    placeholder="100"
                    style={inputStyle}
                  />
                </FormGroup>
              </div>
            ) : (
              // Non-multi-motor: use the existing raw readout
              <>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 14, color: Colors.WHITE, opacity: 0.8, marginBottom: 4 }}>
                    Position Sensor Reading
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 'bold', color: Colors.WHITE }}>
                    {positionSensorValue !== null ? positionSensorValue.toFixed(4) : '---'} rad
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 32, marginBottom: 24 }}>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{ fontSize: 14, color: Colors.WHITE, opacity: 0.8, marginBottom: 4 }}
                    >
                      Min Position
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff6b6b' }}>
                      {minPosition !== null ? minPosition.toFixed(4) : '---'}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{ fontSize: 14, color: Colors.WHITE, opacity: 0.8, marginBottom: 4 }}
                    >
                      Max Position
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#69db7c' }}>
                      {maxPosition !== null ? maxPosition.toFixed(4) : '---'}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Start Homing Button */}
            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
              <Button
                backgroundColor={isHoming ? Colors.YELLOW : Colors.BUTTON_GREEN}
                textColor={isHoming ? Colors.BLACK : Colors.WHITE}
                onClick={handleStartHoming}
                disabled={isHoming || !connectedDevice?.handle || isJogHoming}
              >
                {isHoming ? 'Homing...' : 'Start Homing'}
              </Button>
              {jogHomingEnabled && (
                <Button
                  backgroundColor={isJogHoming ? Colors.YELLOW : Colors.BUTTON_GREEN}
                  textColor={isJogHoming ? Colors.BLACK : Colors.WHITE}
                  onClick={isJogHoming ? handleStopJogHoming : () => setShowJogWarningModal(true)}
                  disabled={isHoming || !connectedDevice?.handle}
                >
                  {isJogHoming ? 'Stop Jog Homing' : 'Jog Homing'}
                </Button>
              )}
            </div>

            {isJogHoming && motorOptions.length > 0 && (
              <div
                style={{
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 12,
                  flexWrap: 'wrap'
                }}
              >
                <FormGroup label="Jog Motor" style={{ marginBottom: 0 }}>
                  <HTMLSelect
                    value={selectedMotorNodeId?.toString() ?? ''}
                    options={motorOptions}
                    onChange={(e) => {
                      const nextId = Number(e.target.value)
                      setSelectedMotorNodeId(Number.isFinite(nextId) ? nextId : null)
                    }}
                    style={{ ...inputStyle, width: 180 }}
                    disabled={isHoming}
                  />
                </FormGroup>
              </div>
            )}

            <Dialog
              isOpen={jogHomingEnabled && showJogWarningModal}
              onClose={() => setShowJogWarningModal(false)}
              style={{
                backgroundColor: '#23272f',
                color: Colors.WHITE,
                borderRadius: 12,
                border: `1px solid ${Colors.LIGHT_GREEN}`,
                boxShadow: '0 8px 28px rgba(0,0,0,0.45)'
              }}
            >
              <div style={{ ...configModalStyle, backgroundColor: 'transparent' }}>
                <div style={{ ...centerItemsStyle, marginBottom: 10 }}>
                  <h3
                    style={{
                      color: Colors.LIGHT_GREEN,
                      textAlign: 'center',
                      margin: 0,
                      letterSpacing: 0.4
                    }}
                  >
                    Jog Homing Safety Warning
                  </h3>
                </div>
                <div style={centerItemsStyle}>
                  <h2 style={{ color: Colors.RED, textAlign: 'center', marginBottom: 8 }}>
                    THIS IS EXTREMELY DANGEROUS.
                  </h2>
                </div>
                <div style={centerItemsStyle}>
                  <p style={{ textAlign: 'center', fontSize: 16, margin: 0, color: Colors.WHITE }}>
                    RECOMMEND TO START AT 25 RPM (MINIMUM SPEED) AND ADJUST ACCORDINGLY.
                  </p>
                </div>
                <div style={{ ...centerItemsStyle, gap: 12, marginTop: 16 }}>
                  <Button
                    backgroundColor={Colors.WHITE}
                    textColor={Colors.BLACK}
                    onClick={() => setShowJogWarningModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    backgroundColor={Colors.BUTTON_GREEN}
                    textColor={Colors.WHITE}
                    onClick={() => {
                      void handleConfirmStartJogHoming()
                    }}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </Dialog>

            {/* Jog Homing Controls */}
            {jogHomingEnabled && isJogHoming && (
              <div
                style={{
                  marginBottom: 20,
                  padding: 20,
                  background: '#23272f',
                  borderRadius: 12,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  maxWidth: 340
                }}
              >
                <div
                  style={{
                    marginBottom: 16,
                    color: Colors.WHITE,
                    fontWeight: 600,
                    fontSize: 18,
                    letterSpacing: 0.5
                  }}
                >
                  Jog Homing Controls
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {!jogEnabled ? (
                    <Button
                      backgroundColor={Colors.BUTTON_GREEN}
                      textColor={Colors.WHITE}
                      onClick={() => setJogEnabled(true)}
                      style={{ minWidth: 90 }}
                      disabled={!isJogHoming}
                    >
                      Start
                    </Button>
                  ) : (
                    <Button
                      backgroundColor={Colors.RED}
                      textColor={Colors.WHITE}
                      onClick={() => {
                        void handleStopJogCommand()
                      }}
                      style={{ minWidth: 90 }}
                      disabled={!isJogHoming}
                    >
                      Stop
                    </Button>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ color: Colors.WHITE, minWidth: 90 }}>Set RPM:</span>
                    <NumericInput
                      fill={false}
                      value={jogThrottle}
                      onValueChange={(valNum, valStr) => {
                        // Allow empty, '-', or valid number
                        if (/^-?\d*$/.test(valStr) || valStr === '' || valStr === '-') {
                          setJogThrottle(valStr)
                        } else {
                          // Only allow valid numbers
                          const val = parseFloat(valStr)
                          if (!isNaN(val)) {
                            handleJogThrottleChange(val)
                          }
                        }
                        if (isFinite(valNum)) {
                          handleJogThrottleChange(valNum)
                        }
                      }}
                      min={undefined}
                      max={undefined}
                      stepSize={1}
                      minorStepSize={0.1}
                      buttonPosition="none"
                      placeholder="Enter RPM"
                      style={{ width: 120 }}
                      disabled={!connectedDevice?.handle || jogEnabled}
                      allowNumericCharactersOnly={false}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
                    <Button
                      backgroundColor={Colors.BUTTON_GREEN}
                      textColor={Colors.WHITE}
                      onClick={handleSetMin}
                      style={{ flex: 1 }}
                      disabled={!isJogHoming}
                    >
                      Set Min
                    </Button>
                    <Button
                      backgroundColor={Colors.BUTTON_GREEN}
                      textColor={Colors.WHITE}
                      onClick={handleSetMax}
                      style={{ flex: 1 }}
                      disabled={!isJogHoming}
                    >
                      Set Max
                    </Button>
                  </div>
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
                    <div
                      style={{
                        color: '#baff80',
                        fontSize: 15,
                        fontWeight: 600,
                        flex: 1,
                        textAlign: 'center',
                        background: '#232',
                        borderRadius: 6,
                        padding: 8
                      }}
                    >
                      Min: <b>{jogHomingMin !== null ? jogHomingMin.toFixed(4) : '---'}</b>
                    </div>
                    <div
                      style={{
                        color: '#baff80',
                        fontSize: 15,
                        fontWeight: 600,
                        flex: 1,
                        textAlign: 'center',
                        background: '#232',
                        borderRadius: 6,
                        padding: 8
                      }}
                    >
                      Max: <b>{jogHomingMax !== null ? jogHomingMax.toFixed(4) : '---'}</b>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Homing Status */}
            {homingStatus !== null && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, color: Colors.WHITE, opacity: 0.8, marginBottom: 4 }}>
                  Status
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 'bold',
                    color:
                      homingStatus === HomingCalibStatus.SUCCESS
                        ? '#69db7c'
                        : homingStatus === HomingCalibStatus.IN_PROGRESS
                          ? Colors.YELLOW
                          : '#ff6b6b'
                  }}
                >
                  {homingStatusToString(homingStatus)}
                </div>
              </div>
            )}

            {/* Zero Offset */}
            {homingZeroOffset !== null && homingStatus === HomingCalibStatus.SUCCESS && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, color: Colors.WHITE, opacity: 0.8, marginBottom: 4 }}>
                  Zero Offset
                </div>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: Colors.WHITE }}>
                  {homingZeroOffset.toFixed(4)} rad
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', width: FORM_WIDTH, marginTop: 16 }}>
        <div style={{ flex: 1 }}>
          <Button
            backgroundColor={Colors.WHITE}
            textColor={Colors.BLACK}
            onClick={prev}
            disabled={prev === undefined}
          >
            Back
          </Button>
        </div>
        <div style={{ width: 8 }} />
        <div style={{ flex: 1 }} />
        <div style={{ width: 8 }} />
        <div style={{ flex: 1 }}>
          <Button
            backgroundColor={Colors.BUTTON_GREEN}
            textColor={Colors.WHITE}
            onClick={next}
            disabled={next === undefined}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
